import ExcelJS from "exceljs"
import { Translation } from "types/questionnaire.types"

// Define the structure of the object resolved by the Promise
interface ImportResult {
  translations: Translation[]
  languages: string[]
}

/**
 * Imports translations from an Excel file and merges them with existing translations.
 *
 * This function reads an Excel file, processes each sheet as a separate language,
 * and updates or adds translations to the existing set. It preserves existing
 * translations that are not present in the imported file.
 *
 * @param file - The Excel file to import.
 * @param currentTranslations - The current set of translations.
 * @returns A promise that resolves to an object containing:
 *   - translations: The updated set of translations.
 *   - languages: The list of languages found in the import (sheet names).
 * @throws If there's an issue reading the file or processing the data.
 */
export const importExcelTranslations = async (
  file: File,
  currentTranslations: Translation[]
): Promise<ImportResult> => {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(arrayBuffer)

    // Create a deep copy of currentTranslations to avoid mutating the original prop directly
    const importedTranslations: Translation[] = JSON.parse(
      JSON.stringify(currentTranslations)
    )
    const languages = new Set<string>()

    // Process each sheet in the workbook
    workbook.eachSheet((worksheet) => {
      const sheetName = worksheet.name

      // Get headers from first row
      const headerRow = worksheet.getRow(1)
      const headers: Record<string, number> = {}

      headerRow.eachCell((cell, colNumber) => {
        const value = cell.value?.toString() || ""
        if (value === "Identifier" || value === "identifier") {
          headers.identifier = colNumber
        } else if (value === "Original" || value === "original") {
          headers.original = colNumber
        } else if (value === sheetName) {
          headers.translation = colNumber
        }
      })

      // Process data rows (starting from row 2)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return // Skip header row

        const identifier = row.getCell(headers.identifier || 1).value?.toString()
        const original = row.getCell(headers.original || 2).value?.toString()
        const translation = row.getCell(headers.translation || 3).value?.toString() || ""

        if (identifier === undefined || original === undefined) {
          console.warn(
            `Skipping row ${rowNumber} due to missing Identifier or Original field`
          )
          return
        }

        let existingItem = importedTranslations.find(
          (item) => item.identifier === identifier
        )

        if (existingItem) {
          // Update existing translation for the current language (sheetName)
          existingItem[sheetName] = translation
          // Ensure MASTER is also updated if it changed in the sheet, or preserve existing
          existingItem.MASTER = original
        } else {
          // Add new translation item
          existingItem = {
            identifier: identifier,
            MASTER: original,
            [sheetName]: translation,
          }
          importedTranslations.push(existingItem)
        }
        languages.add(sheetName)
      })
    })

    return {
      translations: importedTranslations,
      languages: Array.from(languages),
    }
  } catch (error) {
    console.error("Error processing Excel data:", error)
    throw error instanceof Error ? error : new Error(String(error))
  }
}
